import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Check, BadgeCheck, ArrowRight } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function Referral() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [refRes, planRes] = await Promise.all([
          api.get(`/ref/${encodeURIComponent(code)}`),
          api.get("/plans"),
        ]);
        if (!alive) return;
        const data = refRes.data;
        if (data.valid) {
          localStorage.setItem("sfx_ref", data.ref_code);
          setInfo(data);
        } else {
          localStorage.removeItem("sfx_ref");
        }
        const partnerPlans = (data.plans && data.plans.length) ? data.plans.map((p) => ({
          id: p.id, name: p.name, price: p.price, days: p.days,
          features: [`${p.days} jou aksè`, "4 Enstriman Premium", "Precision Scalping Engine", "Auto SL/TP"],
        })) : (planRes.data || []);
        setPlans(partnerPlans);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [code]);

  const goRegister = () => navigate("/register");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060c18]">
        <Loader2 className="h-7 w-7 animate-spin text-[#1C8CFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060c18] text-white">
      <div className="max-w-4xl mx-auto px-5 py-10">
        {/* Invitation banner */}
        <div className="text-center mb-8 fade-up" data-testid="referral-landing">
          <img src="/smartfx-logo.png" alt="Smart FX" className="h-12 mx-auto mb-4" />
          {info ? (
            <>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1C8CFF]/15 text-[#8fc4ff] text-xs font-semibold mb-3">
                <BadgeCheck className="h-3.5 w-3.5" /> Envitasyon Ofisyèl
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold">Ou envite pa {info.partner_name || info.ref_code}</h1>
              {info.company && <p className="text-[#8fb4e6] mt-1">{info.company}</p>}
              <p className="text-[#8fb4e6] mt-3 max-w-xl mx-auto text-sm">
                Rantre nan <strong className="text-white">Smart Scalper Fx</strong> — AutoTrade pou kont MT5 / Prop Firm ou.
                Chwazi yon plan epi kite entèlijans lan trade pou ou.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold">Byenveni nan Smart Scalper Fx</h1>
              <p className="text-[#8fb4e6] mt-3 text-sm">Lyen sa a pa valab ankò — men ou ka toujou kreye yon kont.</p>
            </>
          )}
          <Button data-testid="ref-register-button" onClick={goRegister} className="mt-6 h-12 px-8 rounded-full bg-[#1C8CFF] hover:bg-[#1C8CFF]/90 text-white font-semibold text-base">
            Kreye Kont Ou <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
          <div className="mt-3 text-sm text-[#8fb4e6]">
            Deja gen kont? <button onClick={() => navigate("/login")} className="text-[#1C8CFF] hover:underline font-semibold">Konekte</button>
          </div>
        </div>

        {/* Plans */}
        {plans.length > 0 && (
          <div>
            <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] text-center mb-4">Chwazi Plan Ou</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((p) => (
                <div key={p.id} data-testid={`ref-plan-${p.id}`} className="border border-[#14233d] bg-gradient-to-b from-[#0B1526] to-[#0A1120] rounded-2xl p-5 flex flex-col">
                  <div className="font-heading text-lg font-bold">{p.name}</div>
                  <div className="mt-1"><span className="font-heading text-3xl font-bold">{money(p.price)}</span> <span className="text-[#5f7ba6] text-sm">/ {p.days}j</span></div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {(p.features || []).slice(0, 6).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-[#c9dcff]"><Check className="h-3.5 w-3.5 text-[#22e07b] shrink-0 mt-0.5" />{f}</li>
                    ))}
                  </ul>
                  <Button onClick={goRegister} className="mt-4 w-full rounded-full bg-[#1C8CFF] hover:bg-[#1C8CFF]/90">Kòmanse</Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
